import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { authAPI, addressesAPI, ordersAPI } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

const GOVERNORATES = ['Capital','Hawalli','Farwaniya','Mubarak Al-Kabeer','Ahmadi','Jahra'];
const TABS = [
  { id: 'profile',   label: 'Profile' },
  { id: 'addresses', label: 'Addresses' },
  { id: 'orders',    label: 'Orders' },
];

const fmtKD = (n) => `KD ${parseFloat(n || 0).toFixed(3)}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '—';
const statusColor = (s) => ({
  pending:    '#999',
  confirmed:  '#3b82f6',
  preparing:  '#a06022',
  shipped:    '#8b5cf6',
  delivered:  '#16a34a',
  cancelled:  '#dc2626',
}[s] || '#666');

const AccountPage = () => {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState('profile');

  return (
    <div className="account-page container">
      <div className="account-header">
        <div>
          <h1>My Account</h1>
          <p style={{ color:'var(--text-muted)', marginTop:4 }}>
            Welcome back{user?.first_name ? `, ${user.first_name}` : ''}.
          </p>
        </div>
        <button className="btn btn-outline" onClick={logout}>Logout</button>
      </div>

      <div className="account-tabs">
        {TABS.map(t => (
          <button
            key={t.id}
            className={`account-tab ${tab === t.id ? 'active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="account-body">
        {tab === 'profile'   && <ProfileSection />}
        {tab === 'addresses' && <AddressesSection />}
        {tab === 'orders'    && <OrdersSection />}
      </div>

      <style>{`
        .account-page { padding: 60px 20px 80px; max-width: 980px; }
        .account-header { display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 28px; gap: 16px; flex-wrap: wrap; }
        .account-header h1 { font-family: var(--font-serif); font-size: 2rem; }
        .account-tabs { display: flex; gap: 4px; border-bottom: 1px solid var(--border); margin-bottom: 28px; flex-wrap: wrap; }
        .account-tab {
          padding: 12px 20px; font-family: var(--font-sans); font-size: 0.92rem; font-weight: 600;
          color: var(--text-muted); border-bottom: 2px solid transparent; transition: all 0.2s;
          background: none; cursor: pointer;
        }
        .account-tab:hover { color: var(--text); }
        .account-tab.active { color: var(--gold); border-bottom-color: var(--gold); }
        .account-card {
          background: white; border-radius: var(--radius-lg); padding: 28px;
          box-shadow: var(--shadow-sm); margin-bottom: 16px;
        }
        .account-card h3 {
          font-family: var(--font-serif); font-size: 1.15rem; margin-bottom: 18px;
          padding-bottom: 12px; border-bottom: 1px solid var(--border);
        }
        .field-row { display: grid; grid-template-columns: 180px 1fr; gap: 12px; padding: 10px 0; border-bottom: 1px solid #f5f5f5; align-items: center; }
        .field-row:last-child { border-bottom: none; }
        .field-row__label { color: var(--text-muted); font-size: 0.86rem; }
        .field-row__value { font-size: 0.94rem; }
        .form-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
        .empty-state { text-align: center; padding: 60px 20px; color: var(--text-muted); }
        .empty-state h4 { font-family: var(--font-serif); font-size: 1.2rem; color: var(--text); margin-bottom: 8px; }
        .addr-list { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 14px; }
        .addr-card {
          background: white; padding: 18px; border-radius: var(--radius-lg); position: relative;
          border: 1px solid var(--border); box-shadow: var(--shadow-sm);
        }
        .addr-card.is-default { border-color: var(--gold); }
        .addr-default-badge {
          position: absolute; top: 12px; right: 12px;
          padding: 2px 8px; background: var(--gold); color: white;
          font-size: 0.7rem; border-radius: 4px; letter-spacing: 0.5px; text-transform: uppercase;
        }
        .addr-card h4 { font-family: var(--font-serif); font-size: 1rem; margin-bottom: 6px; }
        .addr-card p { font-size: 0.86rem; line-height: 1.5; color: var(--text-muted); }
        .addr-actions { display: flex; gap: 8px; margin-top: 14px; flex-wrap: wrap; }
        .addr-actions button {
          padding: 6px 12px; background: var(--cream); font-size: 0.78rem; border-radius: 6px;
          font-weight: 600; cursor: pointer; transition: all 0.2s;
        }
        .addr-actions button:hover { background: var(--gold); color: white; }
        .addr-actions .danger:hover { background: #dc2626; color: white; }
        .order-row {
          display: grid; grid-template-columns: 1fr auto auto auto auto; gap: 16px;
          align-items: center; padding: 14px 0; border-bottom: 1px solid #f5f5f5;
        }
        .order-row:last-child { border-bottom: none; }
        .order-num { font-family: monospace; font-weight: 600; font-size: 0.9rem; }
        .order-status { font-size: 0.78rem; font-weight: 600; padding: 3px 10px; border-radius: 99px; color: white; text-transform: capitalize; }
        .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 2000; display: flex; align-items: center; justify-content: center; padding: 20px; }
        .modal { background: white; border-radius: var(--radius-lg); width: 100%; max-width: 560px; max-height: 90vh; overflow-y: auto; }
        .modal-header { display: flex; justify-content: space-between; align-items: center; padding: 18px 24px; border-bottom: 1px solid var(--border); }
        .modal-header h3 { font-family: var(--font-serif); }
        .modal-body { padding: 24px; }
        @media (max-width: 600px) {
          .field-row { grid-template-columns: 1fr; }
          .form-grid-2 { grid-template-columns: 1fr; }
          .order-row { grid-template-columns: 1fr; gap: 8px; }
        }
      `}</style>
    </div>
  );
};

// ─── PROFILE TAB ─────────────────────────────────────────────
const ProfileSection = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ first_name: '', last_name: '', phone: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    authAPI.getProfile()
      .then(p => { setProfile(p); setForm({ first_name: p.first_name || '', last_name: p.last_name || '', phone: p.phone || '' }); })
      .catch(err => toast.error(err.message))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await authAPI.updateProfile(form);
      setProfile(p => ({ ...p, ...form }));
      setEditing(false);
      toast.success('Profile updated');
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="account-card"><div className="spinner" style={{ margin:'40px auto' }} /></div>;
  if (!profile) return <div className="account-card empty-state"><p>Could not load profile.</p></div>;

  return (
    <div className="account-card">
      <h3>Personal Information</h3>

      {!editing ? (
        <>
          <div className="field-row">
            <div className="field-row__label">Email</div>
            <div className="field-row__value">{profile.email}</div>
          </div>
          <div className="field-row">
            <div className="field-row__label">First Name</div>
            <div className="field-row__value">{profile.first_name || <em style={{ color:'var(--text-muted)' }}>not set</em>}</div>
          </div>
          <div className="field-row">
            <div className="field-row__label">Last Name</div>
            <div className="field-row__value">{profile.last_name || <em style={{ color:'var(--text-muted)' }}>not set</em>}</div>
          </div>
          <div className="field-row">
            <div className="field-row__label">Phone</div>
            <div className="field-row__value">{profile.phone || <em style={{ color:'var(--text-muted)' }}>not set</em>}</div>
          </div>
          <div className="field-row">
            <div className="field-row__label">Member Since</div>
            <div className="field-row__value">{fmtDate(profile.created_at)}</div>
          </div>
          <button className="btn btn-primary" style={{ marginTop: 18 }} onClick={() => setEditing(true)}>Edit Profile</button>
        </>
      ) : (
        <form onSubmit={handleSave}>
          <div className="form-grid-2">
            <div className="form-group">
              <label className="form-label">First Name</label>
              <input className="form-control" value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Last Name</label>
              <input className="form-control" value={form.last_name} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Phone</label>
            <input className="form-control" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+965 XXXX XXXX" />
          </div>
          <p style={{ fontSize:'0.82rem', color:'var(--text-muted)', marginBottom: 12 }}>
            Email cannot be changed. Contact support if you need a new email on this account.
          </p>
          <div style={{ display:'flex', gap:12 }}>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</button>
            <button type="button" className="btn btn-outline" onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </form>
      )}
    </div>
  );
};

// ─── ADDRESSES TAB ───────────────────────────────────────────
const emptyAddr = { label: 'Home', full_name: '', phone: '', address_line1: '', address_line2: '', area: '', governorate: 'Capital', country: 'Kuwait', is_default: 0 };

const AddressesSection = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyAddr);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    addressesAPI.getAll()
      .then(setList)
      .catch(err => toast.error(err.message))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(emptyAddr); setEditingId(null); setShowForm(true); };
  const openEdit = (a) => { setForm({ ...emptyAddr, ...a }); setEditingId(a.id); setShowForm(true); };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? (checked ? 1 : 0) : value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) await addressesAPI.update(editingId, form);
      else            await addressesAPI.create(form);
      toast.success(editingId ? 'Address updated' : 'Address added');
      setShowForm(false);
      load();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (a) => {
    if (!window.confirm(`Delete this address (${a.label})?`)) return;
    try { await addressesAPI.delete(a.id); toast.success('Address deleted'); load(); }
    catch (err) { toast.error(err.message); }
  };

  const handleSetDefault = async (a) => {
    try {
      await addressesAPI.update(a.id, { ...a, is_default: 1 });
      toast.success(`${a.label} set as default`);
      load();
    } catch (err) { toast.error(err.message); }
  };

  return (
    <div>
      <div className="account-card">
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 18, paddingBottom: 12, borderBottom: '1px solid var(--border)' }}>
          <h3 style={{ borderBottom: 'none', padding: 0, marginBottom: 0 }}>Saved Addresses</h3>
          <button className="btn btn-primary" onClick={openNew}>+ Add Address</button>
        </div>

        {loading ? (
          <div className="spinner" style={{ margin:'40px auto' }} />
        ) : list.length === 0 ? (
          <div className="empty-state">
            <h4>No saved addresses yet</h4>
            <p>Save your delivery address for faster checkout.</p>
          </div>
        ) : (
          <div className="addr-list">
            {list.map(a => (
              <div key={a.id} className={`addr-card ${a.is_default ? 'is-default' : ''}`}>
                {a.is_default ? <span className="addr-default-badge">Default</span> : null}
                <h4>{a.label}</h4>
                {a.full_name && <p style={{ fontWeight:600, color:'var(--text)' }}>{a.full_name}</p>}
                <p>
                  {a.address_line1}<br/>
                  {a.address_line2 ? <>{a.address_line2}<br/></> : null}
                  {[a.area, a.governorate, a.country].filter(Boolean).join(', ')}
                </p>
                {a.phone && <p>📞 {a.phone}</p>}
                <div className="addr-actions">
                  <button onClick={() => openEdit(a)}>Edit</button>
                  {!a.is_default && <button onClick={() => handleSetDefault(a)}>Set Default</button>}
                  <button className="danger" onClick={() => handleDelete(a)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showForm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>{editingId ? 'Edit Address' : 'New Address'}</h3>
              <button onClick={() => setShowForm(false)}>✕</button>
            </div>
            <form onSubmit={handleSave} className="modal-body">
              <div className="form-grid-2">
                <div className="form-group">
                  <label className="form-label">Label</label>
                  <input name="label" value={form.label} onChange={handleChange} className="form-control" placeholder="Home, Office..." />
                </div>
                <div className="form-group">
                  <label className="form-label">Full Name</label>
                  <input name="full_name" value={form.full_name || ''} onChange={handleChange} className="form-control" />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Phone</label>
                <input name="phone" value={form.phone || ''} onChange={handleChange} className="form-control" placeholder="+965 XXXX XXXX" />
              </div>
              <div className="form-group">
                <label className="form-label">Address Line 1 *</label>
                <input name="address_line1" value={form.address_line1} onChange={handleChange} className="form-control" required placeholder="Block, Street, Building" />
              </div>
              <div className="form-group">
                <label className="form-label">Address Line 2</label>
                <input name="address_line2" value={form.address_line2 || ''} onChange={handleChange} className="form-control" placeholder="Apt, Floor (optional)" />
              </div>
              <div className="form-grid-2">
                <div className="form-group">
                  <label className="form-label">Area</label>
                  <input name="area" value={form.area || ''} onChange={handleChange} className="form-control" placeholder="Salmiya" />
                </div>
                <div className="form-group">
                  <label className="form-label">Governorate</label>
                  <select name="governorate" value={form.governorate} onChange={handleChange} className="form-control">
                    {GOVERNORATES.map(g => <option key={g} value={g}>{g}</option>)}
                  </select>
                </div>
              </div>
              <label className="checkbox-label" style={{ display:'flex', alignItems:'center', gap:8, marginTop:8 }}>
                <input type="checkbox" name="is_default" checked={!!form.is_default} onChange={handleChange} />
                Set as default address
              </label>
              <div style={{ display:'flex', gap:12, marginTop:20 }}>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : (editingId ? 'Update' : 'Save Address')}</button>
                <button type="button" className="btn btn-outline" onClick={() => setShowForm(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── ORDERS TAB ──────────────────────────────────────────────
const OrdersSection = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    ordersAPI.getMyOrders()
      .then(setOrders)
      .catch(err => toast.error(err.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="account-card"><div className="spinner" style={{ margin:'40px auto' }} /></div>;

  return (
    <div className="account-card">
      <h3>My Orders {orders.length > 0 ? <span style={{ fontSize:'0.9rem', color:'var(--text-muted)', fontFamily:'var(--font-sans)', fontWeight:'normal' }}>({orders.length})</span> : null}</h3>

      {orders.length === 0 ? (
        <div className="empty-state">
          <h4>No orders yet</h4>
          <p>When you place an order it'll appear here.</p>
          <Link to="/shop" className="btn btn-primary" style={{ marginTop: 16, display: 'inline-block' }}>Start Shopping</Link>
        </div>
      ) : (
        orders.map(o => (
          <div key={o.id || o.order_number} className="order-row">
            <div>
              <div className="order-num">#{o.order_number}</div>
              <div style={{ fontSize:'0.78rem', color:'var(--text-muted)', marginTop:2 }}>{fmtDate(o.created_at)}</div>
            </div>
            <div style={{ fontSize:'0.84rem', color:'var(--text-muted)' }}>{o.item_count || o.items_count || ''} {o.item_count ? 'item(s)' : ''}</div>
            <div><strong>{fmtKD(o.total)}</strong></div>
            <span className="order-status" style={{ background: statusColor(o.status) }}>{o.status}</span>
            <Link to={`/checkout/success?order=${o.order_number}`} className="btn btn-outline" style={{ padding:'6px 14px', fontSize:'0.82rem' }}>View</Link>
          </div>
        ))
      )}
    </div>
  );
};

export default AccountPage;
